const RN_BLOCKED_SEARCH_HANDLES = new Set([
  'concrete-pet-bowl',
  'custom-photo-to-bust-sculpture',
  'fairy-mushroom-hamster-hide',
  'personalised-gold-bust-trophy',
  'personalized-graffiti-pet-bowl',
  'moon-familiar-planter-photo-to-custom-plant-pot',
]);

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.cachedMap = new Map();
    this.abortController = null;
    this.requestSequence = 0;
    this.ignoreNextFocus = false;
    this.closedByEscape = false;
    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };

    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.searchScope = ['memorial', 'rat', 'home'].includes(this.dataset.rnSearchScope)
      ? this.dataset.rnSearchScope
      : 'neutral';
    this.searchLabel = this.dataset.rnSearchLabel || 'all products';
    this.browseLabel = this.dataset.rnBrowseLabel || 'Choose a range';
    this.browseUrl = this.dataset.rnBrowseUrl || FoxTheme.routes.root_url || '/';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');
    this.isHeaderSearch = this.closest('header') !== null;
    this.inputElement = this.input;
    this.formElement = this.form;
    this.resetButtonElement = this.resetButton;

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    this.boundHandleDocumentKeydown = this.handleDocumentKeydown.bind(this);
    this.boundHandleDocumentFocusIn = this.handleDocumentFocusIn.bind(this);
    this.boundHandleMenuDrawerOpen = this.handleMenuDrawerOpen.bind(this);
    this.boundHandleInputKeydown = this.handleInputKeydown.bind(this);
    this.boundClear = this.clear.bind(this);

    if (this.resetButtonElement) {
      this.resetButtonElement.addEventListener('click', this.boundClear);
    }
    if (this.inputElement) {
      this.inputElement.addEventListener(
        'input',
        FoxTheme.utils.debounce(this.onChange.bind(this), 300)
      );
      this.inputElement.addEventListener('focus', this.onFocus.bind(this));
      this.inputElement.addEventListener('keydown', this.boundHandleInputKeydown);
    }
    if (this.formElement) {
      this.formElement.addEventListener('submit', this.onSubmit.bind(this));
    }

    document.addEventListener('click', this.boundHandleClickOutside);
    // Capture Escape before another header/menu listener can consume it. The
    // input handler still owns arrow-key result navigation; this document-level
    // capture is only for closing the currently open predictive panel.
    document.addEventListener('keydown', this.boundHandleDocumentKeydown, true);
    document.addEventListener('focusin', this.boundHandleDocumentFocusIn);
    document.addEventListener('menu-drawer:open', this.boundHandleMenuDrawerOpen);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    document.removeEventListener('click', this.boundHandleClickOutside);
    document.removeEventListener('keydown', this.boundHandleDocumentKeydown, true);
    document.removeEventListener('focusin', this.boundHandleDocumentFocusIn);
    document.removeEventListener('menu-drawer:open', this.boundHandleMenuDrawerOpen);
    this.resetButtonElement?.removeEventListener('click', this.boundClear);
    this.inputElement?.removeEventListener('keydown', this.boundHandleInputKeydown);
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get form() {
    return this.querySelector('form');
  }

  get resultsId() {
    return 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
  }

  get resultsElement() {
    return document.getElementById(this.resultsId);
  }

  getQuery() {
    return this.inputElement?.value.trim() || '';
  }

  onFocus() {
    if (this.ignoreNextFocus) {
      this.ignoreNextFocus = false;
      return;
    }

    this.closedByEscape = false;

    if (this.header) {
      document.documentElement.style.setProperty('--viewport-height', window.innerHeight + 'px');
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        parseInt(this.header.getBoundingClientRect().bottom, 10) + 'px'
      );
    }

    this.toggleSearchState(true);
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent?.classList.add('hidden');
      }
      return;
    }

    this.searchContent?.classList.remove('hidden');
    this.renderSection(this.setupURL().toString());
  }

  clear(event = null) {
    event?.preventDefault();
    this.abortController?.abort();
    this.abortController = null;
    this.requestSequence += 1;

    if (this.inputElement) {
      this.inputElement.value = '';
    }
    if (this.resultsElement) {
      this.resultsElement.innerHTML = '';
    }
    this.removeAttribute('results');
    this.removeAttribute('error');
    this.setLoadingState(false);
    this.toggleSearchState(false, true);
  }

  onSubmit(event) {
    if (!this.formElement) return;

    event.preventDefault();
    const url = new URL(
      this.formElement.getAttribute('action') || FoxTheme.routes.search_url,
      window.location.origin
    );
    url.search = '';
    const query = this.getQuery();

    if (query) {
      url.searchParams.set('q', query);
    }
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    if (this.searchScope !== 'neutral') {
      url.searchParams.set('view', this.searchScope);
    }
    window.location.assign(url.toString());
  }

  setupURL() {
    const url = new URL(window.shopUrl + FoxTheme.routes.predictive_search_url);
    url.searchParams.set('q', this.getQuery());
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  onChange() {
    this.closedByEscape = false;
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }

    this.searchContent?.classList.remove('hidden');
    this.renderSection(this.setupURL().toString());
  }

  renderSection(url) {
    const requestSequence = ++this.requestSequence;
    this.abortController?.abort();
    this.abortController = null;

    if (this.cachedMap.has(url)) {
      this.renderSectionFromCache(url, requestSequence);
      return;
    }

    this.renderSectionFromFetch(url, requestSequence);
  }

  renderSectionFromCache(url, requestSequence) {
    if (requestSequence !== this.requestSequence) return;
    this.setLoadingState(false);
    this.renderSearchResults(this.cachedMap.get(url));
  }

  renderSectionFromFetch(url, requestSequence) {
    const controller = new AbortController();
    this.abortController = controller;
    this.setLoadingState(true);

    fetch(url, {
      signal: controller.signal,
      headers: { Accept: 'text/html' },
    })
      .then((response) => {
        if (!response.ok) throw new Error('Predictive search request failed');
        return response.text();
      })
      .then((responseText) => {
        if (requestSequence !== this.requestSequence) return;
        this.cachedMap.set(url, responseText);
        this.renderSearchResults(responseText);
      })
      .catch((error) => {
        if (error.name === 'AbortError' || requestSequence !== this.requestSequence) return;
        console.warn('Predictive search is temporarily unavailable.', error);
        this.renderUnavailableState();
      })
      .finally(() => {
        if (requestSequence === this.requestSequence) {
          this.setLoadingState(false);
        }
        if (this.abortController === controller) {
          this.abortController = null;
        }
      });
  }

  renderSearchResults(responseText) {
    if (this.closedByEscape) return;
    const targetElement = this.resultsElement;
    if (!targetElement || !responseText) {
      this.renderUnavailableState();
      return;
    }

    const parser = new DOMParser();
    const parsedDoc = parser.parseFromString(responseText, 'text/html');
    const contentElement = parsedDoc.getElementById(this.resultsId);

    if (!contentElement) {
      this.renderUnavailableState();
      return;
    }

    this.searchContent?.classList.remove('hidden');
    targetElement.innerHTML = contentElement.innerHTML;
    this.applyWorldScope();
    this.removeAttribute('error');
    this.setAttribute('results', '');
    this.toggleSearchState(true);
    this.clearActiveOption();
  }

  getIntentOwner() {
    const query = this.getQuery().toLowerCase();
    if (!query || this.searchScope === 'neutral') return '';

    if (this.searchScope === 'memorial') {
      if (query.includes('rat hide') || query.includes('rat hammock') || query.includes('rat cage')) return '';
      if (query.includes('urn')) return 'memorial-urns';
      if (query.includes('stone') || query.includes('marker') || query.includes('plaque')) return 'memorial-markers';
      if (query.includes('bust') || query.includes('portrait') || query.includes('sculpture')) return 'memorial-pet-busts';
    }

    if (this.searchScope === 'rat') {
      if (query.includes('urn') || query.includes('memorial') || query.includes('planter') || query.includes('garden ornament') || query.includes('garden sculpture')) return '';
      if (query.includes('hide') || query.includes('house') || query.includes('hammock') || query.includes('bed')) return 'rat-hides-beds';
      if (query.includes('shelf') || query.includes('ledge') || query.includes('climb') || query.includes('perch')) return 'rat-shelves-climbing';
      if (query.includes('enrichment') || query.includes('forage') || query.includes('foraging') || query.includes('toy') || query.includes('play')) return 'rat-enrichment-toys';
      if (query.includes('kit') || query.includes('bundle') || query.includes('starter set')) return 'rat-starter-sets';
    }

    if (this.searchScope === 'home') {
      if (query.includes('pet urn') || query.includes('memorial urn') || query.includes('rat hide') || query.includes('rat cage')) return '';
      if (query.includes('planter') || query.includes('plant pot')) return 'home-planters';
      if (query.includes('garden ornament') || query.includes('garden sculpture') || query.includes('garden statue')) return 'home-garden-ornaments';
      if (query.includes('wall art') || query.includes('mirror')) return 'home-wall-art-mirrors';
    }

    return '';
  }

  isForceEmptyIntent() {
    const query = this.getQuery().toLowerCase();
    if (!query || this.searchScope === 'neutral') return false;
    if (this.searchScope === 'memorial') {
      return query.includes('rat hide') || query.includes('rat hammock') || query.includes('rat cage');
    }
    if (this.searchScope === 'rat') {
      return query.includes('urn') || query.includes('memorial') || query.includes('planter') || query.includes('garden ornament') || query.includes('garden sculpture');
    }
    if (this.searchScope === 'home') {
      return query.includes('pet urn') || query.includes('memorial urn') || query.includes('rat hide') || query.includes('rat cage');
    }
    return false;
  }

  getProductHandle(option) {
    if (option.dataset.rnProductHandle) return option.dataset.rnProductHandle;
    const href = option.querySelector('a[href*="/products/"]')?.getAttribute('href') || '';
    return href.split('/products/')[1]?.split(/[?#]/)[0] || '';
  }

  applyWorldScope() {
    if (!this.resultsElement) return;

    const options = Array.from(this.resultsElement.querySelectorAll('[data-rn-product-world]'));
    const metadataOption = options.find(
      (option) => option.dataset.rnQueryIntentOwner || option.dataset.rnQueryForceEmpty
    );
    const intentOwner = metadataOption?.dataset.rnQueryIntentOwner || this.getIntentOwner();
    const forceEmpty = metadataOption?.dataset.rnQueryForceEmpty === 'true' || this.isForceEmptyIntent();
    let visibleCount = 0;

    options.forEach((option) => {
      const isAssigned =
        option.dataset.rnAssignmentState !== 'blocked_unassigned_review' &&
        !RN_BLOCKED_SEARCH_HANDLES.has(this.getProductHandle(option));
      const isInWorld = this.searchScope === 'neutral' || option.dataset.rnProductWorld === this.searchScope;
      const isOwnerMatch = !intentOwner || option.dataset.rnProductOwner === intentOwner;
      const isMatch = !forceEmpty && isAssigned && isInWorld && isOwnerMatch;
      option.hidden = !isMatch;
      option.setAttribute('aria-hidden', isMatch ? 'false' : 'true');
      if (isMatch) visibleCount += 1;
    });

    if (options.length > 0 && visibleCount === 0) {
      this.renderScopedEmptyState();
    }
  }

  renderScopedEmptyState() {
    const targetElement = this.resultsElement;
    if (!targetElement) return;

    const emptyState = document.createElement('div');
    emptyState.className = 'rn-search-empty empty-state flex flex-col gap-3 w-full h-full';

    const heading = document.createElement('h4');
    heading.textContent = `No matching products in ${this.searchLabel} for “${this.getQuery()}”`;

    const guidance = document.createElement('p');
    guidance.className = 'm-0 text-subtext';
    guidance.textContent = 'Try another product name or browse the current range.';

    const submitButton = document.createElement('button');
    submitButton.className = 'btn btn--primary';
    submitButton.type = 'button';
    submitButton.textContent = 'See all results';
    submitButton.addEventListener('click', () => this.formElement?.requestSubmit());

    const browseLink = document.createElement('a');
    browseLink.className = 'btn btn--secondary';
    browseLink.href = this.browseUrl;
    browseLink.textContent = this.browseLabel;

    emptyState.append(heading, guidance, submitButton, browseLink);
    targetElement.replaceChildren(emptyState);
  }

  renderUnavailableState() {
    if (this.closedByEscape) return;
    const targetElement = this.resultsElement;
    if (!targetElement) return;

    const emptyState = document.createElement('div');
    emptyState.className = 'rn-search-empty empty-state flex flex-col gap-3 w-full h-full';

    const heading = document.createElement('h4');
    heading.textContent = 'Search isn’t available right now.';

    const guidance = document.createElement('p');
    guidance.className = 'm-0 text-subtext';
    guidance.textContent = `Try again or browse ${this.searchLabel}.`;

    const retryButton = document.createElement('button');
    retryButton.type = 'button';
    retryButton.className = 'btn btn--secondary rn-search-retry';
    retryButton.textContent = 'Try again';

    emptyState.append(heading, guidance, retryButton);
    targetElement.replaceChildren(emptyState);
    targetElement.querySelector('.rn-search-retry')?.addEventListener('click', () => {
      if (this.getQuery()) {
        this.renderSection(this.setupURL().toString());
      }
    });
    const browseLink = document.createElement('a');
    browseLink.className = 'btn btn--secondary';
    browseLink.href = this.browseUrl;
    browseLink.textContent = this.browseLabel;
    targetElement.querySelector('.rn-search-empty')?.appendChild(browseLink);
    this.searchContent?.classList.remove('hidden');
    this.setAttribute('error', 'true');
    this.setAttribute('results', '');
    this.toggleSearchState(true);
  }

  handleInputKeydown(event) {
    if (this.isEscapeKey(event) && this.classList.contains(this.states.OPEN)) {
      event.preventDefault();
      event.stopPropagation();
      this.closeFromKeyboard();
      return;
    }

    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;

    const options = this.getResultOptions();
    if (options.length === 0) return;

    event.preventDefault();
    const activeId = this.inputElement?.getAttribute('aria-activedescendant');
    let index = options.findIndex((option) => option.id === activeId);
    index += event.key === 'ArrowDown' ? 1 : -1;

    if (index < 0) index = options.length - 1;
    if (index >= options.length) index = 0;
    this.setActiveOption(options[index]);
  }

  handleDocumentKeydown(event) {
    if (!this.isEscapeKey(event) || !this.classList.contains(this.states.OPEN)) return;

    event.preventDefault();
    event.stopPropagation();
    this.closeFromKeyboard();
  }

  isEscapeKey(event) {
    return event?.key === 'Escape' || event?.code === 'Escape';
  }

  closeFromKeyboard() {
    this.closedByEscape = true;
    this.abortController?.abort();
    this.abortController = null;
    this.requestSequence += 1;
    this.toggleSearchState(false, true);
  }

  handleMenuDrawerOpen() {
    this.toggleSearchState(false);
  }

  getResultOptions() {
    return Array.from(this.resultsElement?.querySelectorAll('[role="option"]') || []).filter((option) => {
      const rect = option.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });
  }

  setActiveOption(option) {
    this.getResultOptions().forEach((result) => {
      result.setAttribute('aria-selected', result === option ? 'true' : 'false');
    });
    if (option?.id && this.inputElement) {
      this.inputElement.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView({ block: 'nearest' });
    }
  }

  clearActiveOption() {
    this.getResultOptions().forEach((result) => result.setAttribute('aria-selected', 'false'));
    this.inputElement?.removeAttribute('aria-activedescendant');
  }

  handleDocumentFocusIn(event) {
    if (!this.isHeaderSearch || !this.classList.contains(this.states.OPEN)) return;

    const target = event.target;
    if (!(target instanceof Element)) return;
    if (this.contains(target) || target.closest('.header__search-close')) return;

    this.toggleSearchState(false);
  }

  handleClickOutside(event) {
    const target = event.target;
    if (!(target instanceof Element) || this.contains(target)) return;

    if (this.isHeaderSearch) {
      const clickedOverlay = target.closest('.fixed-overlay--header-section');
      const clickedClose = target.closest('.header__search-close');
      if (clickedOverlay || clickedClose) {
        this.toggleSearchState(false, true);
      }
      return;
    }

    this.toggleSearchState(false);
  }

  toggleSearchState(isOpen, restoreFocus = false) {
    this.classList.toggle(this.states.OPEN, isOpen);
    this.inputElement?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }

    if (!isOpen) {
      this.clearActiveOption();
      this.removeAttribute('results');
      this.removeAttribute('error');
      this.searchContent?.classList.add('hidden');
      if (restoreFocus) {
        this.focusInput();
      }
    }
  }

  focusInput() {
    if (!this.inputElement || document.activeElement === this.inputElement) return;
    this.ignoreNextFocus = true;
    this.inputElement.focus({ preventScroll: true });
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton?.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButtonElement?.classList.add(this.states.LOADING);
      this.resultsElement?.setAttribute('aria-busy', 'true');
      return;
    }

    this.removeAttribute('loading');
    this.resetButtonElement?.classList.remove(this.states.LOADING);
    this.resultsElement?.removeAttribute('aria-busy');
    if (this.getQuery()) {
      this.setAttribute('results', '');
    }
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
